import type { ClaimWorkFindingHighlight, ClaimWorkItem } from '../store';

/**
 * THE presentation adapter over the canonical claim-work row (server
 * `claim_work_items`, PR smartscribe-server#4202).
 *
 * The same row powers list rows, queue/filter counts, saved-view counts,
 * batch-submit validation, AND the workspace detail's `work_summary` — so its
 * presentation vocabulary lives here ONCE. The claims worklist and the claim
 * workbench must read this module rather than keeping page-local label maps:
 * the list and the detail behave as one product, and a claim's state, counts,
 * and submission decision must not change just because someone opened it.
 */

/** `work_state` values the canonical view emits (claim_work_items.sql). */
export const WORK_STATE_LABELS: Record<string, string> = {
  draft: 'Draft',
  needs_correction: 'Needs correction',
  payment_posted: 'Payment posted',
  ready_to_submit: 'Ready to submit',
  submission_processing: 'Submission processing',
  with_payer: 'With payer',
};

export const WORK_STATE_COLORS: Record<
  string,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  draft: 'default',
  needs_correction: 'error',
  payment_posted: 'success',
  ready_to_submit: 'success',
  submission_processing: 'info',
  with_payer: 'info',
};

/**
 * `submit_block_reason` is a closed enum on the canonical view; these are the
 * biller-facing fallbacks for each. Where the row carries the server's own
 * specific message (`submission_top_blocker` / `legacy_top_blocker`),
 * `submitBlockLabel` prefers it over these generics — "Billing provider NPI
 * is required" beats "Required claim information is missing".
 */
const SUBMIT_BLOCK_REASON_LABELS: Record<string, string> = {
  claim_status: 'This claim’s status doesn’t allow submission.',
  not_scrubbed: 'Checks haven’t run against this claim yet.',
  claim_validation: 'The claim failed validation checks.',
  submission_validation: 'Required claim information is missing.',
  certification_finding: 'Open blockers must be resolved first.',
  not_ready: 'This claim isn’t ready to submit.',
};

function submitBlockLabel(item: ClaimWorkItem): string | null {
  if (item.can_submit) return null;
  const reason = item.submit_block_reason ?? null;
  if (reason === 'submission_validation' && item.submission_top_blocker) {
    return item.submission_top_blocker;
  }
  if (reason === 'claim_validation' && item.legacy_top_blocker) {
    return item.legacy_top_blocker;
  }
  if (reason && SUBMIT_BLOCK_REASON_LABELS[reason]) return SUBMIT_BLOCK_REASON_LABELS[reason];
  /* An unknown (or absent) reason on a non-submittable row still refuses
     honestly — the server said no; the copy just can't say why. */
  return 'Submission isn’t available for this claim.';
}

export interface ClaimWorkSubmitModel {
  /** The server's decision, verbatim. Never re-derived client-side. */
  canSubmit: boolean;
  /** Raw `submit_block_reason` enum value, for logic/analytics. */
  blockReason: string | null;
  /** Biller-facing copy for the refusal — null exactly when `canSubmit`. */
  blockLabel: string | null;
}

export interface ClaimWorkSummaryModel {
  /** Raw `work_state` (kept for logic), plus its shared label/tone. */
  workState: string;
  stateLabel: string;
  stateTone: 'default' | 'success' | 'warning' | 'error' | 'info';
  claimLifecycle: string;
  /** Payer outcome, or null when the view says 'none'. */
  payerOutcome: string | null;
  /** current | stale | missing — the canonical row's freshness. */
  certificationFreshness: string;
  /** checking | complete | idle — persisted run state (survives reload). */
  certificationRunState: string;
  certificationDecision: string | null;
  /**
   * Server-authoritative counts (`active_*_count`). NEVER derived from
   * `findingHighlights.length` — highlights are a capped preview, not the
   * population.
   */
  counts: { findings: number; blockers: number; advisories: number };
  findingHighlights: ClaimWorkFindingHighlight[];
  submit: ClaimWorkSubmitModel;
  requiresCorrection: boolean;
}

/**
 * `work_summary` is null when the claim has no canonical row (e.g. a
 * superseded claim — the view excludes them). Callers treat null as "no
 * canonical answer", never as "no problems".
 */
export function deriveClaimWorkSummary(
  item: ClaimWorkItem | null | undefined,
): ClaimWorkSummaryModel | null {
  if (!item) return null;
  return {
    workState: item.work_state,
    stateLabel: WORK_STATE_LABELS[item.work_state] ?? item.work_state,
    stateTone: WORK_STATE_COLORS[item.work_state] ?? 'default',
    claimLifecycle: item.claim_lifecycle,
    payerOutcome: item.payer_outcome === 'none' ? null : item.payer_outcome,
    certificationFreshness: item.certification_freshness,
    certificationRunState: item.certification_run_state,
    certificationDecision: item.certification_decision ?? null,
    counts: {
      findings: item.active_finding_count ?? 0,
      blockers: item.active_blocker_count ?? 0,
      advisories: item.active_advisory_count ?? 0,
    },
    findingHighlights: item.finding_highlights ?? [],
    submit: {
      canSubmit: item.can_submit,
      blockReason: item.submit_block_reason ?? null,
      blockLabel: submitBlockLabel(item),
    },
    requiresCorrection: item.requires_correction,
  };
}
